import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography
} from '@mui/material';
import { DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import {
  PREINVOICE_STATUSES,
  deletePreinvoiceEntry,
  subscribeToRecentPreinvoices,
  updatePreinvoiceStatus
} from '../utils/preinvoiceUtils';
import {
  INVOICE_STATUSES,
  deleteInvoiceEntry,
  subscribeToRecentInvoices,
  updateInvoiceStatus
} from '../utils/invoiceUtils';

const LIST_TYPES = {
  preinvoice: {
    key: 'preinvoice',
    label: 'Išankstinės sąskaitos',
    title: 'Išankstinės sąskaitos',
    statuses: PREINVOICE_STATUSES,
    updateStatus: updatePreinvoiceStatus,
    deleteEntry: deletePreinvoiceEntry
  },
  invoice: {
    key: 'invoice',
    label: 'Sąskaitos faktūros',
    title: 'Sąskaitos faktūros',
    statuses: INVOICE_STATUSES,
    updateStatus: updateInvoiceStatus,
    deleteEntry: deleteInvoiceEntry
  }
};

export default function InvoiceDocumentsList({
  enabled,
  currentYear,
  readOnly = false,
  headerTitle = 'Išsiųstos sąskaitos',
  emptyHint = 'Dar nėra įrašų.',
  preinvoiceSubscribe = subscribeToRecentPreinvoices,
  invoiceSubscribe = subscribeToRecentInvoices
}) {
  const [listType, setListType] = useState('preinvoice');
  const [preinvoices, setPreinvoices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState('');
  const [statusUpdatingId, setStatusUpdatingId] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState('');

  useEffect(() => {
    if (!enabled) {
      setPreinvoices([]);
      setInvoices([]);
      return undefined;
    }
    const unsubPre = preinvoiceSubscribe(currentYear, setPreinvoices);
    const unsubInv = invoiceSubscribe(currentYear, setInvoices);
    return () => {
      unsubPre();
      unsubInv();
    };
  }, [currentYear, enabled, preinvoiceSubscribe, invoiceSubscribe]);

  const config = LIST_TYPES[listType];
  const items = listType === 'preinvoice' ? preinvoices : invoices;

  const handleStatusChange = async (docId, status) => {
    setStatusUpdatingId(docId);
    try {
      await config.updateStatus(currentYear, docId, status);
    } catch (err) {
      setError(err.message || 'Nepavyko pakeisti statuso.');
    } finally {
      setStatusUpdatingId('');
    }
  };

  const openDeleteDialog = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirmed = async () => {
    if (!itemToDelete) return;
    const deletingItem = itemToDelete;
    setDeletingId(deletingItem.id);
    setDeleteDialogOpen(false);
    setItemToDelete(null);
    try {
      await config.deleteEntry(currentYear, deletingItem);
    } catch (err) {
      setError(err.message || 'Nepavyko ištrinti sąskaitos.');
    } finally {
      setDeletingId('');
    }
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2.5, mb: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {headerTitle}
          </Typography>
          <ButtonGroup variant="outlined" size="small">
            {Object.values(LIST_TYPES).map((type) => (
              <Button
                key={type.key}
                variant={listType === type.key ? 'contained' : 'outlined'}
                onClick={() => setListType(type.key)}
              >
                {type.label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {config.title} — naujausi viršuje.
        </Typography>

        <Stack spacing={1}>
          {items.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {emptyHint}
            </Typography>
          )}
          {items.map((item) => (
            <Paper
              key={item.id}
              variant="outlined"
              sx={{
                p: 1.5,
                borderColor: item.isTest ? 'warning.main' : 'divider',
                borderWidth: item.isTest ? 2 : 1
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box>
                  {!readOnly && (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.clientName || 'Klientas'} ({item.clientEmail})
                    </Typography>
                  )}
                  {readOnly && (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.invoiceNumber || item.id}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {!readOnly && `${item.invoiceNumber || item.id} | `}
                    {Number(item.amountEur || 0).toFixed(2)} Eur
                    {item.invoiceDate ? ` | ${item.invoiceDate}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    label={item.status || '—'}
                    color={item.status === 'sendError' ? 'error' : item.status === 'Payed' ? 'success' : 'default'}
                  />
                  {!readOnly && item.emailSent === false && (
                    <Chip size="small" label="Be el. laiško" variant="outlined" />
                  )}
                  {!readOnly && (
                    <Select
                      size="small"
                      value={item.status || 'pending payment'}
                      disabled={statusUpdatingId === item.id}
                      onChange={(e) => handleStatusChange(item.id, e.target.value)}
                    >
                      {config.statuses.map((st) => (
                        <MenuItem key={st} value={st}>{st}</MenuItem>
                      ))}
                    </Select>
                  )}
                  {item.pdfUrl && (
                    <Button size="small" href={item.pdfUrl} target="_blank" rel="noopener noreferrer">
                      PDF
                    </Button>
                  )}
                  {!readOnly && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => openDeleteDialog(item)}
                      disabled={statusUpdatingId === item.id || deletingId === item.id}
                    >
                      Ištrinti
                    </Button>
                  )}
                </Box>
              </Box>
              {item.sendError && (
                <Typography variant="caption" color="error.main">
                  sendError: {item.sendError}
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>
      </Paper>

      {!readOnly && (
        <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
          <DialogTitle>Ištrinti sąskaitą?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Bus ištrinti sąskaitos įrašas, susiję `mail` laiškai ir PDF failas.
            </Typography>
            {itemToDelete && (
              <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                {itemToDelete.clientName || 'Klientas'} ({itemToDelete.clientEmail || 'be el. pašto'}) — {itemToDelete.invoiceNumber || itemToDelete.id}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog} disabled={Boolean(deletingId)}>Atšaukti</Button>
            <Button color="error" variant="contained" onClick={handleDeleteConfirmed} disabled={Boolean(deletingId)}>
              Ištrinti
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
